import { DdiziType, GuestUserType } from '@kinderlabs-pos/shared-data-type';
import { getUuidV4 } from '@kinderlabs-pos/shared-util';
import { StoreInfoState } from '@kinderlabs-pos/state';
import { DatePicker, WithDialog, XlDialogProps } from '@kinderlabs-pos/ui-atoms';
import { FullSizeCircularProgress } from '@kinderlabs-pos/ui-components';
import {
	Button,
	DialogActions,
	DialogContent,
	Divider,
	FormControlLabel,
	ListItemButton,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { MappingTicketQuery } from '../../../mappingTicket/mappingTicketQuery';
import { GenderType } from '../../../mappingTicket/mappingTicketType';

type UserToRenderType = {
	tempUuid: string;
	id?: number;
	orderId?: string;
	mappedTicketId?: string;
	name: string;
	birth: string;
	gender: GenderType;
};
export const VaunceDdiziMappingDialog = ({
	open,
	closeDialog,
	orderId,
	ddizis,
	users: usersFromWaitingList,
	...others
}: XlDialogProps & {
	orderId: string;
	ddizis: DdiziType[];
	users: GuestUserType[];
}) => {
	const { storeId } = useAtomValue(StoreInfoState.curStoreAndDevice);

	const { data, isLoading, refetch } = useQuery({
		...MappingTicketQuery.query.mappingTicket.getTicketMappingInfoForOrders(orderId),
		suspense: false,
		refetchOnWindowFocus: true,
	});

	const 새로만드는건가 = !data || data.length === 0;

	const createMemberBulkMutation = MappingTicketQuery.useCreateMemberBulk({
		onSuccess: (data) => {
			refetch();
			closeDialog?.();
		},
	});

	const [지금선택해야하는Ddizi, set지금선택해야하는Ddizi] = useState<DdiziType | null>(null);

	const [usersToRender, setUsersToRender] = useState<UserToRenderType[]>([]);
	useEffect(() => {
		const 주문수정의경우이미매핑된티켓리스트를제외해보자 = (data ?? []).filter(
			(d) => !ddizis.some((ddizi) => d.mappedTicketId === ddizi.id)
		);
		const result: UserToRenderType[] = (data ?? [])
			.filter((d) => {
				return !주문수정의경우이미매핑된티켓리스트를제외해보자.some((dd) => dd.id === d.id);
			})
			.map((item) => {
				return {
					tempUuid: getUuidV4(),
					...item,
				};
			});

		usersFromWaitingList
			.filter((user) => {
				return !(data ?? []).some((item) => item.name === user.name);
			})
			.forEach((user) => {
				result.push({
					tempUuid: getUuidV4(),
					name: user.name ?? '',
					birth: user.birthday ?? '',
					gender: user.gender === 'MAN' ? 'MALE' : 'FEMALE',
				});
			});

		setUsersToRender(result);
	}, [data, ddizis, usersFromWaitingList]);

	const handleSubmit = async () => {
		// if (새로만드는건가)
		await createMemberBulkMutation.mutateAsync({
			storeId,
			orderId,
			ticketList: ddizis.map((ddizi) => {
				return {
					id: ddizi.id,
					label: ddizi.name,
					minutes: ddizi.minutes,
					published: ddizi.publishInfo.published,
					ticketType: ddizi.type,
					available: ddizi.available,
				};
			}),
			memberList: usersToRender.map((user) => {
				return {
					tempUuid: user.tempUuid,
					memberId: user.id,
					userName: user.name,
					userBirth: user.birth,
					userGender: user.gender,
				};
			}),
			initialMemberTicketMapping: usersToRender
				.filter((user) => user.mappedTicketId)
				.reduce((acc, user) => {
					acc[user.mappedTicketId!] = user.tempUuid;
					return acc;
				}, {} as Record<string, string>),
		});

		closeDialog?.();
	};

	return (
		<WithDialog.XlDialog
			{...others}
			open={open}
			closeDialog={closeDialog}
			maxWidth='md'
			dialogTitle={'고객 매핑 (보호자권은 고객을 매핑하지 않습니다.)'}>
			<DialogContent
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}
				sx={{
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'row',
					width: 624,
					gap: 1,
				}}>
				{isLoading && <FullSizeCircularProgress />}
				{!isLoading && (
					<Stack
						direction={'row'}
						sx={{ gap: 1, width: '100%', height: '100%' }}>
						<Stack
							height={'100%'}
							overflow={'auto'}
							flex={1}>
							{ddizis
								.filter((ddizi) => ddizi.type !== 'PARENTS')
								.map((ddizi) => {
									const ddiziMapped = usersToRender.find(
										(item) => item.mappedTicketId === ddizi.id
									);
									return (
										<ListItemButton
											selected={ddizi.id === 지금선택해야하는Ddizi?.id}
											onClick={() => {
												set지금선택해야하는Ddizi(ddizi);
											}}
											key={ddizi.id}>
											<Stack sx={{ width: '100%', overflowX: 'hidden' }}>
												<Typography
													sx={{
														textOverflow: 'ellipsis',
														overflow: 'hidden',
														whiteSpace: 'nowrap',
													}}>
													{ddizi.name}
												</Typography>
												<Typography sx={{ color: 'gray' }}>{`#${ddizi.id}`}</Typography>
												{ddiziMapped?.name ? (
													<Stack
														direction={'row'}
														gap={1}>
														<Typography>{ddiziMapped.name}</Typography>
														<Button
															size='small'
															sx={{ px: 1, py: 0 }}
															onClick={(e) => {
																e.stopPropagation();
																setUsersToRender((prev) =>
																	prev.map((prevItem) =>
																		prevItem.mappedTicketId === ddizi.id
																			? {
																					...prevItem,
																					mappedTicketId: undefined,
																					orderId: undefined,
																			  }
																			: prevItem
																	)
																);
															}}>
															매핑 해제
														</Button>
													</Stack>
												) : (
													<Typography>고객 매핑 없음</Typography>
												)}
											</Stack>
										</ListItemButton>
									);
								})}
						</Stack>
						<Divider orientation='vertical' />
						<Stack
							overflow={'auto'}
							height={'100%'}
							flex={1}>
							{usersToRender.map((user, index) => {
								return (
									<ListItemButton key={index}>
										<Stack alignItems={'start'}>
											<Typography>{`${user.name} / ${user.birth} / ${
												user.gender ? (user.gender === 'MALE' ? '남자' : '여자') : ''
											}`}</Typography>
											<Typography sx={{ color: 'gray' }}>
												{user.mappedTicketId ? `${user.mappedTicketId} 매핑됨` : '매핑 필요'}
											</Typography>
											<Stack
												direction={'row'}
												sx={{ gap: 2 }}>
												<Button
													disabled={!지금선택해야하는Ddizi}
													size='small'
													color={user.mappedTicketId ? 'warning' : 'primary'}
													onClick={(e) => {
														e.stopPropagation();

														if (!지금선택해야하는Ddizi) return;

														setUsersToRender((prev) =>
															prev.map((prevItem, indexToUpdate) =>
																indexToUpdate === index
																	? {
																			...prevItem,
																			mappedTicketId: 지금선택해야하는Ddizi.id,
																			orderId: 지금선택해야하는Ddizi.publishInfo.orderId,
																	  }
																	: prevItem.mappedTicketId === 지금선택해야하는Ddizi.id
																	? {
																			...prevItem,
																			mappedTicketId: undefined,
																			orderId: undefined,
																	  }
																	: prevItem
															)
														);

														set지금선택해야하는Ddizi(null);
													}}>
													{user.mappedTicketId ? '매핑 변경' : '매핑 선택'}
												</Button>
												<WithDialog
													dialog={(open, closeDialog) => (
														// eslint-disable-next-line react/jsx-pascal-case
														<바운스고객매핑DialogContent
															open={open}
															closeDialog={closeDialog}
															orderId={orderId}
															type='EDIT'
															user={user}
															setUser={(user) => {
																setUsersToRender((prev) =>
																	prev.map((item) =>
																		item.tempUuid === user.tempUuid ? { ...item, ...user } : item
																	)
																);
															}}
														/>
													)}>
													{(openDialog) => (
														<Button
															size='small'
															onClick={openDialog}>
															정보 수정
														</Button>
													)}
												</WithDialog>
												<Button
													size='small'
													onClick={() => {
														setUsersToRender((prev) =>
															prev.filter((item) => item.tempUuid !== user.tempUuid)
														);
													}}>
													정보 삭제
												</Button>
											</Stack>
										</Stack>
									</ListItemButton>
								);
							})}
						</Stack>
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Stack
					width={'100%'}
					justifyContent={'flex-end'}
					spacing={2}
					px={2}
					direction={'row'}>
					<WithDialog
						dialog={(open, closeDialog) => (
							// eslint-disable-next-line react/jsx-pascal-case
							<바운스고객매핑DialogContent
								open={open}
								closeDialog={closeDialog}
								orderId={orderId}
								type='ADD'
								user={undefined}
								setUser={(user) => {
									setUsersToRender((prev) => [
										...prev,
										{ ...user, tempUuid: getUuidV4() } as UserToRenderType,
									]);
								}}
							/>
						)}>
						{(openDialog) => (
							<Button
								variant='contained'
								onClick={openDialog}>
								고객 추가
							</Button>
						)}
					</WithDialog>

					<Button
						variant='contained'
						onClick={handleSubmit}>
						{새로만드는건가 ? '매핑 완료' : '매핑 수정'}
					</Button>
					<Button
						variant='outlined'
						onClick={closeDialog}>
						닫기
					</Button>
				</Stack>
			</DialogActions>
		</WithDialog.XlDialog>
	);
};

type USERTYPE = {
	name: string;
	birth?: string;
	gender?: GenderType;
	tempUuid?: string;
};

const 바운스고객매핑DialogContent = ({
	type,
	user,
	setUser,
	open,
	closeDialog,
}: XlDialogProps & {
	orderId: string;
	setUser: (users: USERTYPE) => void;
} & (
		| {
				type: 'ADD';
				user: undefined;
		  }
		| {
				type: 'EDIT';
				user: USERTYPE;
		  }
	)) => {
	const [form, setForm] = useState<USERTYPE>(
		user ?? { name: '', birth: dayjs().format('YYYYMMDD') }
	);
	return (
		<WithDialog.XlDialog
			open={open}
			dialogTitle={type === 'ADD' ? '고객 추가' : '고객 편집'}>
			<DialogContent sx={{ minWidth: 428 }}>
				<Stack
					height={'100%'}
					sx={{ gap: 2, p: 2 }}>
					<TextField
						label='이름'
						value={form.name}
						onChange={(e) => {
							setForm({ ...form, name: e.target.value });
						}}
					/>
					<DatePicker
						value={form.birth ? dayjs(form.birth) : null}
						minDate={dayjs('1950-01-01')}
						maxDate={dayjs()}
						onChange={(value) => {
							if (value) setForm({ ...form, birth: value.format('YYYYMMDD') });
						}}
						usageType={'POS'}
					/>
					<Stack spacing={0.5}>
						<Typography variant='h6'>{'성별'}</Typography>
						<RadioGroup
							row
							sx={{ width: '100%' }}
							value={form.gender}>
							<FormControlLabel
								sx={{ flex: 1, justifyContent: 'center' }}
								value='MALE'
								control={
									<Radio onChange={() => setForm((prev) => ({ ...prev, gender: 'MALE' }))} />
								}
								label='남성'
							/>
							<FormControlLabel
								sx={{ flex: 1, justifyContent: 'center' }}
								value='FEMALE'
								control={
									<Radio onChange={() => setForm((prev) => ({ ...prev, gender: 'FEMALE' }))} />
								}
								label='여성'
							/>
						</RadioGroup>
					</Stack>
				</Stack>
				<DialogActions>
					<Button
						variant='contained'
						disabled={!form.name || !form.birth || !form.gender}
						onClick={() => {
							setUser(form);
							closeDialog?.();
						}}>
						확인
					</Button>
					<Button
						variant='outlined'
						onClick={closeDialog}>
						닫기
					</Button>
				</DialogActions>
			</DialogContent>
		</WithDialog.XlDialog>
	);
};
